import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChakraComponents as CC } from '@Modules/core/lib/chakra';
import { Box, IconButton, useColorModeValue } from '@chakra-ui/react';
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
} from 'react-icons/fa';
import React from 'react';

type Props = {
  data: any[],
  columns: any,
  hasFooter?: boolean,
  [x: string]: any,
  hasPagination?: boolean,
  initialColumnVisibility?: {[x: string]: boolean}
};

function CustomReactTable({
  columns,
  data,
  hasFooter,
  hasPagination = true,
  initialColumnVisibility,
  ...rest
}: Props) {
  const [columnVisibility, setColumnVisibility] = React.useState(initialColumnVisibility || {})

  // const borderColor = useColorModeValue('gray.300', 'gray.300');
  const table = useReactTable({
    data,
    columns,

    // Pipeline
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    //
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    
    // debugTable: true,
  });
  
  const { pageSize, pageIndex } = table.getState().pagination;
  const bcolor = useColorModeValue('var(--chakra-colors-gray-200)', 'var(--chakra-colors-gray-700)');

  // Render the UI for your table
  return (
    <>
      {/* For show/hide column */}
      {/* 
        <div className="inline-block border border-black shadow rounded" style={{display: ""}}>
          <div className="px-1 border-b border-black">
            <label>
              <input
                {...{
                  type: 'checkbox',
                  checked: table.getIsAllColumnsVisible(),
                  onChange: table.getToggleAllColumnsVisibilityHandler(),
                }}
              />{' '}
              Toggle All
            </label>
          </div>
          {table.getAllLeafColumns().map(column => {
            
            return (
              <div key={column.id} className="px-1">
                <label>
                  <input
                    {...{
                      type: 'checkbox',
                      checked: column.getIsVisible(),
                      onChange: column.getToggleVisibilityHandler(),
                    }}
                  />{' '}
                  {column.id}
                </label>
              </div>
            )
          })}
        </div> 
      */}

      <CC.Box
        bg='chakra-body-bg'
        boxShadow={{ base: 'none', md: 'base' }}
        borderTop={`1px solid ${bcolor}`}
        py={5}
        mb={5}
      >
        <CC.TableContainer shadow="sm" {...rest}>
          <CC.Table>
            <CC.Thead>
              {/* TODO: Implement column sorting feature */}
              {table.getHeaderGroups().map((headerGroup) => (
                <CC.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <CC.Th
                      key={header.id}
                      colSpan={header.colSpan}
                      fontWeight="bold"
                      color="blue.500"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </CC.Th>
                  ))}
                </CC.Tr>
              ))}
            </CC.Thead>
            <CC.Tbody color="gray.500">
              {table.getRowModel().rows.map((row, i) => {
                return (
                  <CC.Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <CC.Td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </CC.Td>
                      );
                    })}
                  </CC.Tr>
                );
              })}
            </CC.Tbody>

            {!!hasFooter && (
              <CC.Tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <CC.Tr key={footerGroup.id}>
                    {footerGroup.headers.map((footer) => (
                      <CC.Th key={footer.id} colSpan={footer.colSpan}>
                        {footer.isPlaceholder
                          ? null
                          : flexRender(
                              footer.column.columnDef.footer,
                              footer.getContext()
                            )}
                      </CC.Th>
                    ))}
                  </CC.Tr>
                ))}
              </CC.Tfoot>
            )}
          </CC.Table>
        </CC.TableContainer>

        {hasPagination && (
          <CC.Flex justifyContent="end" mt={4} mr={4} alignItems="center">
            <CC.Flex>
              <CC.Tooltip label="First Page">
                <IconButton
                  onClick={() => table.setPageIndex(0)}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<FaAngleDoubleLeft width={3} height={3} />}
                  mr={4}
                  aria-label="Go to first page arrow button"
                />
              </CC.Tooltip>
              <CC.Tooltip label="Previous Page">
                <IconButton
                  onClick={table.previousPage}
                  isDisabled={!table.getCanPreviousPage()}
                  icon={<FaAngleLeft height={6} width={6} />}
                  aria-label="Go to previous page arrow button"
                />
              </CC.Tooltip>
            </CC.Flex>

            <CC.Flex alignItems="center" mx={5}>
              <CC.Text flexShrink="0" mr={'5'}>
                Page{' '}
                <CC.Text fontWeight="bold" as="span">
                  {pageIndex + 1}
                </CC.Text>{' '}
                de{' '}
                <CC.Text fontWeight="bold" as="span">
                  {table.getPageOptions().length}
                </CC.Text>
              </CC.Text>
              <CC.Text flexShrink="0">Go to page:</CC.Text>{' '}
              <CC.NumberInput
                mx={5}
                w={28}
                min={1}
                max={table.getPageOptions().length}
                onChange={(value) => {
                  const page = value ? parseInt(value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                defaultValue={pageIndex + 1}
              >
                <CC.NumberInputField />
                <CC.NumberInputStepper>
                  <CC.NumberIncrementStepper />
                  <CC.NumberDecrementStepper />
                </CC.NumberInputStepper>
              </CC.NumberInput>
              <CC.Select
                w={32}
                value={pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              >
                {[5, 10, 25, 50, { label: 'All', value: data.length }].map(
                  (pageSize, index) => {
                    const size =
                      typeof pageSize === 'number' ? pageSize : pageSize.value;
                    return (
                      <option key={index} value={size}>
                        Show{' '}
                        {typeof pageSize === 'number'
                          ? pageSize
                          : pageSize.label}
                      </option>
                    );
                  }
                )}
              </CC.Select>
            </CC.Flex>

            <CC.Flex>
              <CC.Tooltip label="Next Page">
                <IconButton
                  onClick={table.nextPage}
                  isDisabled={!table.getCanNextPage()}
                  icon={<FaAngleRight height={6} width={6} />}
                  aria-label="Go to next page arrow button"
                />
              </CC.Tooltip>
              <CC.Tooltip label="Last Page">
                <CC.IconButton
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  isDisabled={!table.getCanNextPage()}
                  icon={<FaAngleDoubleRight height={3} width={3} />}
                  ml={4}
                  aria-label="Go to last page arrow button"
                />
              </CC.Tooltip>
            </CC.Flex>
          </CC.Flex>
        )}
      </CC.Box>
    </>
  );
}

export default CustomReactTable;
